import React, { useEffect, useRef } from 'react'
import { daysAgo, timeToSell } from '../../helpers/daysAgo'
import { getGalleryImageUrl, getGalleryImageAlt } from '../../helpers/galleryHelpers'
import PropertyValuationSnippet from './PropertyValuationSnippet'

const List = ({ properties, smallImages, total, hasMore, loadingMore, loadMoreError, onLoadMore }) => {
  const observerRef = useRef()
  const loadingRef = useRef()

  // The sentinel now requests the next page from the API instead of revealing
  // items that were already downloaded.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loadMoreError) onLoadMore()
      },
      { threshold: 0.1 }
    )

    if (loadingRef.current) {
      observer.observe(loadingRef.current)
    }

    observerRef.current = observer

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [hasMore, loadingMore, loadMoreError, onLoadMore])

  return (
    <div className="w-full px-2 sm:px-4 md:px-4">
      <div>
        {properties.map((item) => (
          <div key={item.propertyId} className="mb-4">
            <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100 overflow-hidden">
              <div className="p-3 sm:p-4 md:p-6">
                <div className="flex flex-col lg:flex-row lg:gap-6">
                  {/* Image Gallery */}
                  <div className={`${smallImages ? 'lg:w-[250px]' : 'lg:w-[400px]'} w-full mb-3 lg:mb-0`}>
                    <div className="rounded-lg overflow-hidden">
                      <img
                        src={getGalleryImageUrl(item.gallery)}
                        alt={getGalleryImageAlt(item.gallery)}
                        className="w-full h-48 sm:h-56 lg:h-64 object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <a
                      href={`/detail/${item.propertyId}`}
                      className="block text-black no-underline hover:text-blue-600 transition-colors duration-200"
                      onClick={(e) => {
                        // Open in new tab if Cmd (Mac) or Ctrl (Windows/Linux) is held
                        if (e.metaKey || e.ctrlKey) {
                          e.preventDefault()
                          window.open(e.currentTarget.href, '_blank', 'noopener,noreferrer')
                        }
                      }}
                    >
                      <h3 className="text-base sm:text-lg font-semibold mb-2 text-gray-800 line-clamp-2">{item.title}</h3>
                      <p className="text-sm sm:text-base text-gray-600 mb-2">{item.location}</p>

                      {/* Price section - mobile responsive */}
                      <div className="mb-2">
                        <div className="text-base sm:text-lg font-bold text-green-600">
                          {item.price?.toLocaleString()} {item.price ? 'Kč' : 'Na dotaz'}
                        </div>
                        {item.m2Price && <div className="text-xs sm:text-sm text-gray-500 mt-1">{item.m2Price.toLocaleString()} Kč/m²</div>}
                      </div>

                      <div className="text-sm text-gray-700 space-y-1">
                        <p>{item.ownership}</p>
                        <p>{item.stavba}</p>
                      </div>
                    </a>
                  </div>

                  <PropertyValuationSnippet property={item} />

                  {/* New Tag, Date, and Time to Sell */}
                  <div className="mt-3 lg:mt-0 lg:w-32">
                    <div className="space-y-2">
                      {new Date(item.created) > new Date(Date.now() - 2 * 24 * 60 * 60 * 1000) && (
                        <span className="inline-block bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Nový</span>
                      )}
                      {item.inactive && <span className="inline-block bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Neaktivní</span>}
                      <p className="text-gray-500 text-xs">Přidáno před: {daysAgo(item.created)}</p>
                      {timeToSell(item.created, item.lastUpdate, item.inactive) && (
                        <div className="text-blue-600 text-xs bg-blue-50 px-2 py-1 rounded">Prodej: {timeToSell(item.created, item.lastUpdate, item.inactive)}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Loading indicator and intersection observer target */}
        {hasMore && (
          <div ref={loadingRef} className="py-4 text-center">
            {loadMoreError ? (
              <button onClick={onLoadMore} className="text-blue-600 hover:text-blue-700 text-sm">
                Načtení selhalo – zkusit znovu
              </button>
            ) : loadingMore ? (
              <div className="text-gray-500">Načítání...</div>
            ) : (
              <div className="text-gray-400 text-xs">
                Zobrazeno {properties.length} z {total} položek
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default List
//  {item.comments?.length > 0 ? (
//   <div className="mt-2 md:mt-0 w-1/3">
//     <strong>Komentáře:</strong> {item.comments.length} {item.analyza && <strong>| Analýza</strong>}
//     <div className="mt-2 space-y-2">
//       {item.comments.map((comment, index) => (
//         <p>{comment.text}</p>
//       ))}
//     </div>
//   </div>
// ) : (
//   <>
//     {item.analyza && (
//       <div className="mt-2 md:mt-0 w-1/3">
//         <strong>Analýza:</strong> {item.analyza?.text.replace(/#|##|###|---|\*\*/g, '').slice(-500)}
//       </div>
//     )}
//   </>
// )}
